import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getTrainings } from '../../../services/trainingService';
import { formatDate } from '../../../utils/Utils';
import PersonCell from '../../../components/ui/PersonCell';

const useTrainingApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getTrainings(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const statusLabel = useMemo(() => ({
    draft: t('training.statusDraft'),
    pending_approval: t('training.statusPendingApproval'),
    published: t('training.statusPublished'),
    archived: t('training.statusArchived'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'title',
      label: t('training.title'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'categoryName',
      label: t('training.category'),
      options: { filter: false, sort: false },
    },
    {
      name: 'versionLabel',
      label: t('training.version'),
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('training.status'),
      options: {
        filter: true,
        sort: true,
        filterType: 'dropdown',
        filterOptions: {
          names: ['draft', 'pending_approval', 'published', 'archived'],
          renderValue: (v) => statusLabel[v] || v,
        },
        customBodyRender: (value, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (value === 'pending_approval' && row?.currentApproverFullName) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{statusLabel[value]}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {t('training.waitingOn')}: {row.currentApproverFullName}
                </span>
              </div>
            );
          }
          return statusLabel[value] || value;
        },
      },
    },
    {
      name: 'createUserFullName',
      label: t('training.createdBy'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.createUserProfilePicture} />
        ), filter: false, sort: false },
    },
    {
      name: 'assignedUserCount',
      label: t('training.assignedUsers'),
      options: { filter: false, sort: true },
    },
    {
      name: 'completionPercentage',
      label: t('training.completionRate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => v == null ? '—' : (
          <span style={{
            color: v >= 80 ? '#16A34A' : v >= 50 ? '#F59E0B' : '#EF4444',
            fontWeight: 700,
          }}>%{v}</span>
        ),
      },
    },
    {
      name: 'createDate',
      label: t('training.createDate'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => formatDate(value, i18n.language),
      },
    },
    {
      name: 'updateDate',
      label: t('training.updateDate'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (v) => v ? formatDate(v, i18n.language) : '—',
      },
    },
  ], [t, i18n.language, statusLabel, data]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  return { columns, data, fetching, pagination, refresh };
};

export default useTrainingApi;
